import React, { useState, useRef, useEffect } from 'react';
import Icon from './Icon';
import { LABELS, LABEL_COLOURS } from '../lib/labels';
import styles from './ContextMenu.module.css';

// Right-click context menu for a message row. All actions map to existing
// handlers in App.js — no new backend calls are introduced.
export default function ContextMenu({
  x,
  y,
  msg,
  onClose,
  onReply,
  onReplyAll,
  onForward,
  onRead,
  onFlag,
  onMarkUnread,
  onMarkDone,
  onDelete,
  onSetLabel,
  customLabels = [],
}) {
  // Built-ins + user-added custom labels, alphabetised. Same shape as
  // FolderTree's merged list so the colour dot lookup works for both.
  const mergedLabels = [
    ...LABELS.map(name => ({ name, colour: LABEL_COLOURS[name] })),
    ...customLabels.map(c => ({ name: c.name, colour: c.colour })),
  ].sort((a, b) => a.name.localeCompare(b.name));
  const [labelSubmenu, setLabelSubmenu] = useState(false);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(() => initialPosition(x, y));
  // On desktop the menu is ~240 px wide; on a 375 px phone it's capped at
  // 92vw by CSS so the JS clamp uses the live menu width ceiling instead
  // of a hard-coded 240. Keeps the menu fully in-viewport on both.
  function initialPosition(cx, cy) {
    const MENU_W = Math.min(280, Math.floor(window.innerWidth * 0.92));
    // Initial estimate: 12 items × 28 px + separators ≈ 360 px. Refined
    // after mount via the actual offsetHeight (see useLayoutEffect below).
    const MENU_H = 360;
    const left = Math.max(8, Math.min(cx, window.innerWidth  - MENU_W - 8));
    const top  = Math.max(8, Math.min(cy, window.innerHeight - MENU_H - 8));
    return { left, top };
  }
  // After mount + after the submenu opens, re-clamp based on the live
  // menu height. This is what fixes "Set label submenu cut off below
  // screen on bottom emails": the inline submenu makes the menu taller
  // by ~210 px (7 labels × 28 px + chrome) so the original `top` value
  // computed against a 360 px estimate is wrong once the submenu is
  // open and the user is right-clicking near the bottom of the list.
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const h = rect.height || 360;
    const w = rect.width  || Math.min(280, Math.floor(window.innerWidth * 0.92));
    setPos(prev => {
      const left = Math.max(8, Math.min(x, window.innerWidth  - w - 8));
      const top  = Math.max(8, Math.min(y, window.innerHeight - h - 8));
      if (left === prev.left && top === prev.top) return prev;
      return { left, top };
    });
  }, [labelSubmenu, x, y]);
  const { left, top } = pos;

  const Item = ({ icon, label, shortcut, onClick, danger }) => (
    <div
      className={`${styles.item} ${danger ? styles.itemDanger : ''}`}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); onClose && onClose(); }}
    >
      <span className={styles.icon}>{icon && <Icon name={icon} size={13} />}</span>
      <span className={styles.label}>{label}</span>
      {shortcut && <span className={styles.shortcut}>{shortcut}</span>}
    </div>
  );

  return (
    <div
      ref={menuRef}
      className={styles.ctx}
      style={{ left, top }}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => e.preventDefault()}
    >
      <Item icon="reply"    label="Reply"     shortcut="⌘R"  onClick={() => onReply(msg)} />
      <Item icon="replyAll" label="Reply All" shortcut="⇧⌘R" onClick={() => onReplyAll(msg)} />
      <Item icon="forward"  label="Forward"   shortcut="⌘F"  onClick={() => onForward(msg)} />
      <div className={styles.sep} />
      <Item
        icon={msg.isRead ? 'envelopeClosed' : 'envelopeOpen'}
        label={msg.isRead ? 'Mark as unread' : 'Mark as read'}
        onClick={() => (msg.isRead ? onMarkUnread(msg.id) : onRead && onRead(msg.id))}
      />
      <Item
        icon="flagOutline"
        label={msg.flagged ? 'Unflag' : 'Flag for follow up'}
        onClick={() => onFlag(msg.id, !msg.flagged)}
      />
      <Item
        icon="check"
        label={msg.completed ? 'Unmark as done' : 'Mark as done'}
        shortcut="Ctrl+D"
        onClick={() => onMarkDone && onMarkDone(msg.id)}
      />
      <div className={styles.sep} />
      <div
        className={styles.item}
        onClick={(e) => { e.stopPropagation(); setLabelSubmenu(s => !s); }}
        onMouseEnter={() => setLabelSubmenu(true)}
      >
        <span className={styles.icon}><Icon name="tag" size={13} /></span>
        <span className={styles.label}>
          Set label{msg.label ? ` (now: ${msg.label})` : ''}
        </span>
        <span className={styles.shortcut}>▸</span>
      </div>
      {labelSubmenu && (
        <div className={styles.submenu}>
          {mergedLabels.map(({ name: l, colour }) => (
            <div
              key={l}
              className={`${styles.item} ${l === msg.label ? styles.itemActive : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onSetLabel) onSetLabel(msg, l);
                onClose && onClose();
              }}
            >
              <span className={styles.icon} aria-hidden>
                <span style={{
                  display: 'inline-block', width: 8, height: 8,
                  borderRadius: '50%', background: colour,
                }} />
              </span>
              <span className={styles.label}>{l}</span>
              {l === msg.label && <span className={styles.shortcut}>✓</span>}
            </div>
          ))}
        </div>
      )}
      <div className={styles.sep} />
      <Item icon="print"     label="Print"  shortcut="⌘P" onClick={onClose} />
      <Item icon="trashThin" label="Delete" shortcut="⌦"  danger onClick={() => onDelete(msg.id)} />
    </div>
  );
}
